import { Card, Stack, Text, Badge } from '@repletoryreach/sdk-ui';
import { useT } from '../../../i18n/I18nProvider';

export interface ProfilePreviewValues {
  about: string;
  address: string;
  description: string;
  email: string;
  vertical: string;
  websites: string[];
  /** Either the picture as Meta serves it, or a local preview of one not yet saved. */
  pictureUrl: string | null;
}

export interface ProfilePreviewProps {
  values: ProfilePreviewValues;
  /** What WhatsApp shows as the name: the number's display name, then the number itself. */
  name: string;
  msisdn?: string | null;
  /** Meta's category codes, translated where the catalogue has a name for them. */
  verticalLabel: (code: string) => string;
}

/**
 * What the recipient sees.
 *
 * Somebody who has just typed a description has no way to tell whether it
 * reads well until it is live on their number and every customer is seeing it.
 * This is the same fields in the order WhatsApp puts them: the picture (a
 * circle, always), the name, the number, the status line, then the rest.
 *
 * It is a likeness, not a screenshot. WhatsApp's own chrome changes between
 * Android, iOS and desktop, and pretending otherwise would be a promise this
 * cannot keep. What it is faithful about is the crop, the order and what is
 * left blank.
 */
export function ProfilePreview({ values, name, msisdn, verticalLabel }: ProfilePreviewProps) {
  const t = useT();
  const websites = values.websites.map((w) => w.trim()).filter(Boolean);
  const hasDetail = Boolean(
    values.description || values.address || values.email || websites.length || values.vertical
  );

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.profile.preview.title')}</Text>

        <div className="rr-waprofile__card">
          <div className="rr-waprofile__avatar">
            {values.pictureUrl
              ? <img src={values.pictureUrl} alt="" />
              : (
                <Text role="caption" c="var(--rr-color-text-muted)">
                  {t('numbers.profile.preview.noPicture')}
                </Text>
              )}
          </div>

          <Stack gap={2} align="center">
            <Text fw="semibold">{name}</Text>
            {msisdn && <Text role="caption" c="var(--rr-color-text-muted)">{msisdn}</Text>}
            <Badge size="sm" variant="light">{t('numbers.profile.preview.businessAccount')}</Badge>
          </Stack>

          {/* The status line, directly under the name, as WhatsApp places it. */}
          {values.about
            ? <Text role="bodySmall" className="rr-waprofile__about">{values.about}</Text>
            : <Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.profile.preview.noAbout')}</Text>}

          {hasDetail ? (
            <div className="rr-waprofile__rows">
              {values.description && (
                <Row label={t('numbers.profile.fields.description')}>{values.description}</Row>
              )}
              {values.address && (
                <Row label={t('numbers.profile.fields.address')}>{values.address}</Row>
              )}
              {websites.length > 0 && (
                <Row label={t('numbers.profile.fields.websites')}>
                  <span className="rr-waprofile__websites">
                    {websites.map((w) => (
                      <a key={w} href={w} target="_blank" rel="noreferrer noopener">{w}</a>
                    ))}
                  </span>
                </Row>
              )}
              {values.email && (
                <Row label={t('numbers.profile.fields.email')}>
                  <a href={`mailto:${values.email}`}>{values.email}</a>
                </Row>
              )}
              {values.vertical && values.vertical !== 'UNDEFINED' && (
                <Row label={t('numbers.profile.fields.vertical')}>{verticalLabel(values.vertical)}</Row>
              )}
            </div>
          ) : (
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('numbers.profile.preview.empty')}
            </Text>
          )}
        </div>

        <Text role="caption" c="var(--rr-color-text-muted)">
          {t('numbers.profile.preview.likeness')}
        </Text>
      </Stack>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rr-waprofile__row">
      <Text role="caption" c="var(--rr-color-text-muted)">{label}</Text>
      <Text role="bodySmall">{children}</Text>
    </div>
  );
}
